/**
 * Evidence for a reviewed alert: frames, and the clip when one exists.
 *
 * Every part is optional and the pane must survive any of it missing — an alert
 * with no frames, no video, or a media URL that 404s is still reviewable from
 * its operational facts, so absence renders an explicit state rather than an
 * empty box that reads as a broken layout.
 *
 * Frames are `object-contain`: cropping evidence to fill a tile can hide the
 * very thing an operator is deciding about.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ImageOff, X } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { formatSiteTime } from "@/alerts/utils/format";
import type { AlertFrame, AlertVideo } from "@/alerts/types/alerts-workspace";

const EmptyEvidence = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-4 py-8 text-center">
    <ImageOff className="h-5 w-5 text-muted-foreground/60" />
    <p className="text-xs text-muted-foreground">{message}</p>
  </div>
);

/**
 * Full-size frame view. Traps focus, closes on Escape, and returns focus to the
 * thumbnail that opened it — an operator stepping frames with the keyboard must
 * not be dropped back at the top of the document.
 */
const Lightbox = ({
  frames,
  index,
  onIndexChange,
  onClose,
  timezone,
}: {
  frames: AlertFrame[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  timezone: string | null;
}) => {
  const { t } = useTranslation("alerts");
  const dialogRef = useRef<HTMLDivElement>(null);
  const frame = frames[index];

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const step = useCallback(
    (delta: number) => {
      const next = index + delta;
      if (next >= 0 && next < frames.length) onIndexChange(next);
    },
    [index, frames.length, onIndexChange],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }
  };

  if (!frame) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t("evidence.dialogLabel", { index: index + 1, total: frames.length })}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4 outline-none"
    >
      <div className="flex items-center justify-between text-xs text-white/80">
        <span>
          {t("evidence.frameOf", { index: index + 1, total: frames.length })}
          {frame.event_frame ? ` · #${frame.event_frame}` : ""} ·{" "}
          {formatSiteTime(frame.timestamp, timezone)}
        </span>
        <button
          type="button"
          aria-label={t("evidence.close")}
          onClick={onClose}
          className="rounded p-1 hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center gap-2">
        <button
          type="button"
          aria-label={t("evidence.previousFrame")}
          disabled={index === 0}
          onClick={() => step(-1)}
          className="rounded-full p-2 text-white disabled:opacity-30 hover:bg-white/10"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <img
          src={frame.signed_url}
          alt={t("evidence.frameAlt", { index: index + 1 })}
          className="mx-auto max-h-full min-h-0 flex-1 object-contain"
        />
        <button
          type="button"
          aria-label={t("evidence.nextFrame")}
          disabled={index >= frames.length - 1}
          onClick={() => step(1)}
          className="rounded-full p-2 text-white disabled:opacity-30 hover:bg-white/10"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

interface AlertEvidenceViewerProps {
  /**
   * The full frame set from canonical detail. `preview` is one frame, NOT the
   * collection, so it is only a fallback when `images` is absent.
   */
  frames: AlertFrame[];
  video?: AlertVideo | null;
  timezone: string | null;
}

export const AlertEvidenceViewer = ({ frames, video, timezone }: AlertEvidenceViewerProps) => {
  const { t } = useTranslation("alerts");
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const thumbRefs = useRef(new Map<number, HTMLButtonElement>());

  const hasFrames = frames.length > 0;
  const hasVideo = !!video?.videoAvailable && !!video.signedUrl && !videoFailed;
  const selected = frames[Math.min(index, frames.length - 1)];

  const closeLightbox = () => {
    setLightboxOpen(false);
    thumbRefs.current.get(index)?.focus();
  };

  if (!hasFrames && !hasVideo) {
    return <EmptyEvidence message={t("evidence.none")} />;
  }

  return (
    <div className="space-y-3">
      {hasFrames && selected && (
        <>
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label={t("evidence.enlarge", { index: index + 1, total: frames.length })}
            className="block w-full overflow-hidden rounded-md border border-border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img
              src={selected.signed_url}
              alt=""
              className="max-h-64 w-full object-contain"
            />
          </button>

          {/* Thumbnails only earn their space when there is a choice to make. */}
          {frames.length > 1 && (
            <div
              className="flex gap-1.5 overflow-x-auto pb-1"
              role="listbox"
              aria-label={t("evidence.framesList")}
            >
              {frames.map((frame, i) => (
                <button
                  key={frame.image_id}
                  ref={(el) => {
                    if (el) thumbRefs.current.set(i, el);
                    else thumbRefs.current.delete(i);
                  }}
                  type="button"
                  role="option"
                  aria-selected={i === index}
                  aria-label={t("evidence.frameOf", { index: i + 1, total: frames.length })}
                  onClick={() => setIndex(i)}
                  className={cn(
                    "h-11 w-16 shrink-0 overflow-hidden rounded border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    i === index ? "border-primary ring-1 ring-primary" : "border-border",
                  )}
                >
                  <img src={frame.signed_url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {hasVideo && video && (
        <video
          src={video.signedUrl}
          controls
          preload="metadata"
          // A dead media URL must degrade to "no clip", not to a broken player.
          onError={() => setVideoFailed(true)}
          className="w-full rounded-md border border-border bg-black"
        />
      )}

      {!hasFrames && (
        <EmptyEvidence message={t("evidence.noFrames")} />
      )}

      {lightboxOpen && (
        <Lightbox
          frames={frames}
          index={index}
          onIndexChange={setIndex}
          onClose={closeLightbox}
          timezone={timezone}
        />
      )}
    </div>
  );
};
